import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { useWindowSize } from "usehooks-ts";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { ActionsDisclosure } from "@/components/sidebar/ActionsDisclosure";
import { LabeledInput } from "@/components/sidebar/LabeledInput";
import { NumberInput } from "@/components/sidebar/NumberInput";
import { ActiveTooltip } from "@/components/tooltip/Tooltip";

import type { DocumentModel } from "@/models/DocumentModel";
import type { PlotlyChartNodeAttrs } from "@corca/tiptap";

function extractTitleText(obj: unknown): string {
  if (typeof obj === "string") return obj;
  if (obj && typeof obj === "object")
    return ((obj as Record<string, unknown>).text as string) ?? "";
  return "";
}

export const PlotlyChartStyle = observer(function PlotlyChartStyle({
  doc,
}: {
  doc: DocumentModel;
}) {
  const t = useTranslations("sidebar.activeNode");
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const { plotlyChartAttrs } = doc.editor.editorState;
  const { tiptap } = doc.editor;

  if (!plotlyChartAttrs || !tiptap) return null;

  const layout = plotlyChartAttrs.plotlyLayout ?? {};

  const updateAttrs = (attrs: Partial<PlotlyChartNodeAttrs>) => {
    tiptap.commands.updateAttributes("plotlyChart", attrs);
  };

  const updateLayout = (patch: Record<string, unknown>) => {
    // Deep merge to preserve nested axis properties (range, type, etc.)
    const merged = { ...layout };
    for (const [key, val] of Object.entries(patch)) {
      if (
        val &&
        typeof val === "object" &&
        !Array.isArray(val) &&
        merged[key] &&
        typeof merged[key] === "object"
      ) {
        merged[key] = { ...merged[key], ...val };
      } else {
        merged[key] = val;
      }
    }
    updateAttrs({ plotlyLayout: merged });
  };

  const title = extractTitleText(layout.title);
  const xAxisTitle = extractTitleText((layout.xaxis as Record<string, unknown>)?.title);
  const yAxisTitle = extractTitleText((layout.yaxis as Record<string, unknown>)?.title);

  return (
    <ActionsDisclosure
      data-panel="plotly_chart_style"
      title={t("chart.title")}
      aria-label={t("chart.title")}
      className="w-full"
      disclosureProps={{ defaultOpen: true }}
    >
      <div className="px-2.25 flex flex-col gap-2">
        {/* Title */}
        <LabeledInput
          label={t("chart.chartTitle")}
          value={title}
          onChange={(val) => updateLayout({ title: { text: val, font: { size: 14 } } })}
        />

        {/* X Axis Label */}
        <LabeledInput
          label={t("chart.xAxisLabel")}
          value={xAxisTitle}
          onChange={(val) => updateLayout({ xaxis: { title: { text: val } } })}
        />

        {/* Y Axis Label */}
        <LabeledInput
          label={t("chart.yAxisLabel")}
          value={yAxisTitle}
          onChange={(val) => updateLayout({ yaxis: { title: { text: val } } })}
        />

        <Sidebar.Delimiter className="mt-1 mb-1 mx-[1px]" />

        {/* Dimensions */}
        <Sidebar.SectionTitle className="text-xxxs text-gray-500 pt-0">
          {t("chart.layout")}
        </Sidebar.SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <ActiveTooltip title={t("fields.tooltips.dimensions.width")} focusDisabled>
            <NumberInput
              value={plotlyChartAttrs.width}
              min={200}
              max={windowWidth}
              aria-label={t("chart.chartWidth")}
              icon={<Icon name="Width" />}
              renderButtons={false}
              onSubmit={(value) => updateAttrs({ width: value })}
            />
          </ActiveTooltip>
          <ActiveTooltip title={t("fields.tooltips.dimensions.height")} focusDisabled>
            <NumberInput
              value={plotlyChartAttrs.height}
              min={150}
              max={windowHeight}
              aria-label={t("chart.chartHeight")}
              icon={<Icon name="Height" />}
              renderButtons={false}
              onSubmit={(value) => updateAttrs({ height: value })}
            />
          </ActiveTooltip>
        </div>
      </div>
    </ActionsDisclosure>
  );
});
